/**
 * DAISY Chatbox Component
 *
 * Main container component for the DAISY chatbot.
 * Orchestrates all child components and manages overall chat state.
 */

import { LitElement, html, css } from 'lit';
import { baseStyles } from '../styles/shared-styles.js';
import { chatStore, MESSAGE_TYPES } from '../stores/chat.store.js';
import { userStore } from '../stores/user.store.js';
import { chatService, NEXT_ACTIONS } from '../services/chat.service.js';
import { getEnabledRiskIndicators } from '../config/risk-indicators.js';

// Import child components
import './chat-header.js';
import './chat-messages.js';
import './chat-input.js';
import './suggestion-box.js';
import './address-card.js';
import './get-started-card.js';
import './feedback-buttons.js';

// Import services
import { genesysService } from '../services/genesys.service.js';
import { anonBusinessRequestService } from '../services/anon-business-request.service.js';
import { alertsService } from '../services/alerts.service.js';
import { configService } from '../config/environment.js';
import { apiService } from '../services/api.service.js';
import { getEndpoint } from '../config/endpoints.js';
import { logger } from '../utils/logger.js';
import { isOutsideResidentialScope } from '../utils/residential-scope.js';
import {
    getLastProjectContext,
    setLastProjectContext,
    clearLastProjectContext,
    sessionStore
} from '../utils/storage.js';

/**
 * DaisyChatbox - Main chatbot container
 * @element daisy-chatbox
 */
export class DaisyChatbox extends LitElement {
    static properties = {
        messages: { type: Array, state: true },
        isLoading: { type: Boolean, state: true },
        isStreaming: { type: Boolean, state: true },
        isHandoverActive: { type: Boolean, state: true },
        isOpen: { type: Boolean, reflect: true },
        suggestions: { type: Array, state: true },
        isInputDisabled: { type: Boolean, state: true },
        showClearConfirm: { type: Boolean, state: true },
        isCreatingBR: { type: Boolean, state: true },
        systemAlertActive: { type: Boolean, state: true },
        showCookieConsent: { type: Boolean, state: true },
        serviceUnavailable: { type: Boolean, attribute: 'service-unavailable' }
    };

    static styles = [
        baseStyles,
        css`
            :host {
                display: block;
                font-family: var(--font-family);
            }

            /* Notify-council outcome text (replaces the old inline styles on the
               success / live-with-council <p>). Token-driven, no inline styles. */
            .notify-council-status {
                margin: 0;
                font-weight: 700;
                color: var(--text-primary);
            }

            .chatbox {
                display: flex;
                flex-direction: column;
                height: 100%;
                background-color: var(--bg-white);
                border-radius: var(--border-radius-lg);
                box-shadow: var(--shadow-lg);
                overflow: hidden;
                touch-action: pan-y;
                overscroll-behavior: none;
            }

            .chatbox__content {
                display: flex;
                flex-direction: column;
                flex: 1;
                overflow: hidden;
            }

            .chatbox__unavailable {
                display: flex;
                flex: 1;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 12px;
                padding: 32px 24px;
                text-align: center;
            }

            .chatbox__unavailable-title {
                margin: 0;
                color: var(--text-primary);
                font-size: 20px;
                font-weight: 700;
            }

            .chatbox__unavailable-message {
                margin: 0;
                color: #4b5563;
                font-size: 14px;
                line-height: 1.6;
            }

            /* Chat Messages Container - from eaidev.css lines 173-183 */
            .chatbox__messages {
                flex: 1;
                overflow-y: auto;
                overflow-x: hidden;
                padding: 10px;
                padding-right: 20px;
                display: flex;
                flex-direction: column;
                flex-grow: 1;
                width: 100%;
                overscroll-behavior: contain;
            }

            .chatbox__messages > * {
                width: 100%;
            }

            .chatbox__messages.scrollbar-hide {
                -ms-overflow-style: none;
                scrollbar-width: none;
            }

            .chatbox__messages.scrollbar-hide::-webkit-scrollbar {
                display: none;
                width: 0;
                height: 0;
            }

            .chatbox__input-wrapper {
                display: flex;
                align-items: flex-start;
                padding: 0 10px 10px;
                gap: 8px;
            }

            .chatbox__input {
                flex: 1;
            }

            .chatbox__alert-banner {
                background-color: #fef3c7;
                border-bottom: 1px solid #f59e0b;
                color: #92400e;
                font-size: 12px;
                line-height: 1.4;
                padding: 8px 12px;
                flex-shrink: 0;
            }

            .chatbox__cookie-banner {
                background-color: rgba(255, 255, 255, 0.98);
                border-bottom: 1px solid #e0e0e0;
                color: #213547;
                font-size: 12px;
                line-height: 1.5;
                padding: 12px;
                flex-shrink: 0;
            }

            .cookie-title {
                font-weight: 600;
                margin-bottom: 4px;
            }

            .cookie-text {
                color: #404040;
                margin-bottom: 8px;
            }

            .cookie-buttons {
                display: flex;
                gap: 6px;
                justify-content: flex-end;
            }

            .cookie-btn {
                padding: 4px 10px;
                border-radius: 6px;
                font-size: 12px;
                font-weight: 400;
                cursor: pointer;
                border: 1px solid transparent;
                font-family: inherit;
            }

            .cookie-btn--primary {
                background-color: #000;
                color: white;
                border-color: #000;
            }

            .cookie-btn--primary:hover {
                background-color: #404040;
            }

            .cookie-btn--secondary {
                background-color: #fff;
                color: #213547;
                border-color: #ccc;
            }

            .cookie-btn--secondary:hover {
                background-color: #e0e0e0;
            }

            .cookie-btn--text {
                background: transparent;
                color: #606060;
                border: none;
            }

            .cookie-btn--text:hover {
                color: #213547;
                background-color: rgba(0, 0, 0, 0.05);
            }

            .chatbox__disclaimer {
                color: #9ca3af;
                font-size: 11px;
                line-height: 1.5;
                text-align: center;
                padding: 12px 8px 4px;
            }

            .chatbox__disclaimer a {
                color: #9ca3af;
                text-decoration: underline;
            }

            .chatbox__disclaimer a:hover {
                color: #6b7280;
            }

            /* Restart Button */
            .restart-btn {
                background: none;
                border: 1px solid #d1d5db;
                width: 40px;
                height: 40px;
                padding: 0;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                border-radius: 10px;
                transition: background-color 0.2s;
                box-sizing: border-box;
            }

            .restart-btn:hover {
                background-color: #f3f4f6;
            }

            .restart-btn svg {
                width: 20px;
                height: 20px;
                fill: none;
                stroke: #666;
                transition: stroke 0.2s;
            }

            .restart-btn:hover svg {
                stroke: #111;
            }

            /* Tooltip */
            .restart-btn::after {
                content: 'Restart chat';
                position: absolute;
                bottom: 100%;
                left: 0;
                background: #333;
                color: white;
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 12px;
                white-space: nowrap;
                opacity: 0;
                visibility: hidden;
                transition:
                    opacity 0.2s,
                    visibility 0.2s;
                pointer-events: none;
                margin-bottom: 4px;
            }

            .restart-btn:hover::after {
                opacity: 1;
                visibility: visible;
            }

            /* Hide when closed */
            :host(:not([isopen])) .chatbox {
                display: none;
            }

            /* Responsive styles */
            @media (max-width: 500px) {
                .chatbox {
                    max-height: none;
                    height: 100%;
                    border-radius: 0;
                }
            }

            /* Confirmation Dialog */
            .confirm-dialog {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.5);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 1000;
                border-radius: var(--border-radius-lg);
            }

            .confirm-dialog__content {
                background: white;
                padding: 24px;
                border-radius: 12px;
                box-shadow:
                    0 20px 25px -5px rgba(0, 0, 0, 0.1),
                    0 10px 10px -5px rgba(0, 0, 0, 0.04);
                max-width: 320px;
                width: 90%;
            }

            .confirm-dialog__title {
                font-size: 18px;
                font-weight: 600;
                color: #1f2937;
                margin: 0 0 8px 0;
            }

            .confirm-dialog__message {
                font-size: 14px;
                color: #6b7280;
                margin: 0 0 20px 0;
                line-height: 1.5;
            }

            .confirm-dialog__actions {
                display: flex;
                gap: 12px;
                justify-content: flex-end;
            }

            .confirm-dialog__btn {
                padding: 8px 16px;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all var(--transition-fast);
                border: none;
            }

            .confirm-dialog__btn--cancel {
                background: #f3f4f6;
                color: #374151;
            }

            .confirm-dialog__btn--cancel:hover {
                background: #e5e7eb;
            }

            .confirm-dialog__btn--confirm {
                background: #ef4444;
                color: white;
            }

            .confirm-dialog__btn--confirm:hover {
                background: #dc2626;
            }
        `
    ];

    constructor() {
        super();
        this.messages = [];
        this.isLoading = false;
        this.isStreaming = false;
        this.isHandoverActive = false;
        this.isOpen = true;
        this.suggestions = [];
        this.isInputDisabled = false; // Track input disabled state for address card
        this.showClearConfirm = false; // Track clear confirmation dialog visibility
        this.isCreatingBR = false; // Track business request creation loading state
        this.systemAlertActive = false;
        this.showCookieConsent = false;
        this.serviceUnavailable = false;
        this._currentAddressCardId = null; // Track current address card message ID
        this._unsubscribe = null;
        this._historyRestorePromise = null;
        this._businessRequestPromise = null;
        this._postAddressPersistencePromise = null;
        this._structuredMigrationPromises = new Map();
        this._activeRequestControllers = new Set();
        // WHY: incremented on every restart/clear so in-flight async work
        // (history fetch, BR creation, migration) can detect it was invalidated
        // while awaiting and refuse to re-populate just-cleared state.
        this._resetGeneration = 0;
        this._boundHandleSuggestions = this._handleSuggestionsEvent.bind(this);
        this._boundHandleNextAction = this._handleNextActionEvent.bind(this);
        this._boundHandleGenesysTyping = this._handleGenesysTyping.bind(this);
        this._genesysTypingMessageId = null;
    }

    connectedCallback() {
        super.connectedCallback();

        if (this.serviceUnavailable) {
            return;
        }

        // Subscribe to chat store updates
        this._unsubscribe = chatStore.subscribe((state) => {
            this.messages = state.messages;
            this.isLoading = state.isLoading;
            this.isStreaming = state.isStreaming;
            this.isHandoverActive = state.isHandoverActive;
            this.suggestions = state.suggestions;
        });

        // Load chat history from storage
        chatStore.loadFromStorage();
        this._restoreLocalMessageState();

        // WHY: only treat a BR as orphaned when it's an anonymous-owned BR
        // with no live anon session. For auth-owned BRs (ownerType === 'user')
        // there is no anon session by design — clobbering them here wiped
        // signed-in users' history on every widget mount.
        if (
            chatStore.businessRequestId &&
            chatStore.ownerType !== 'user' &&
            !anonBusinessRequestService.hasActiveSession()
        ) {
            this._clearChatForNewSession();
        } else if (chatStore.businessRequestId) {
            const restorePromise = this._restoreBusinessRequestHistory(chatStore.businessRequestId).finally(() => {
                if (this._historyRestorePromise === restorePromise) {
                    this._historyRestorePromise = null;
                }
            });
            this._historyRestorePromise = restorePromise;
        } else if (getLastProjectContext()) {
            const restorePromise = this._restoreContinuationState().finally(() => {
                if (this._historyRestorePromise === restorePromise) {
                    this._historyRestorePromise = null;
                }
            });
            this._historyRestorePromise = restorePromise;
        } else if (this.messages.length === 0) {
            this._addWelcomeMessages();
            this._showAddressCard();
        }

        // Check for system alerts on load, then poll every 5 minutes
        this._checkSystemAlerts();
        this._alertsInterval = setInterval(() => this._checkSystemAlerts(), 5 * 60 * 1000);

        // Show cookie consent banner if not yet consented
        this._initCookieConsent();

        // Listen for chat service events
        document.addEventListener('chat-suggestions', this._boundHandleSuggestions);
        document.addEventListener('chat-next-action', this._boundHandleNextAction);
        document.addEventListener('genesys-typing', this._boundHandleGenesysTyping);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribe) {
            this._unsubscribe();
        }
        if (this._alertsInterval) {
            clearInterval(this._alertsInterval);
        }
        document.removeEventListener('chat-suggestions', this._boundHandleSuggestions);
        document.removeEventListener('chat-next-action', this._boundHandleNextAction);
        document.removeEventListener('genesys-typing', this._boundHandleGenesysTyping);
        if (this._genesysTypingMessageId) {
            chatStore.finalizeStreamingMessage(this._genesysTypingMessageId);
            this._genesysTypingMessageId = null;
        }
    }

    _handleGenesysTyping(e) {
        const isTyping = !!e.detail?.isTyping;
        if (isTyping) {
            if (!this._genesysTypingMessageId) {
                this._genesysTypingMessageId = chatStore.createStreamingMessage();
            }
        } else if (this._genesysTypingMessageId) {
            chatStore.finalizeStreamingMessage(this._genesysTypingMessageId);
            this._genesysTypingMessageId = null;
        }
    }

    /**
     * Check for active system alerts
     */
    async _checkSystemAlerts() {
        const result = await alertsService.checkAlerts();
        this.systemAlertActive = result?.hasAlerts || false;
    }

    // --- Cookie Consent ---

    _initCookieConsent() {
        try {
            const consent = localStorage.getItem('clarity_cookie_consent');
            if (!consent) {
                setTimeout(() => {
                    this.showCookieConsent = true;
                }, 1000);
            } else {
                const parsed = JSON.parse(consent);
                if (parsed.version === '1.0') {
                    this._applyClarityConsent(parsed);
                } else {
                    setTimeout(() => {
                        this.showCookieConsent = true;
                    }, 1000);
                }
            }
        } catch {
            setTimeout(() => {
                this.showCookieConsent = true;
            }, 1000);
        }
    }

    _handleCookieAcceptAll() {
        const consent = { analytics: true, advertising: true, version: '1.0', timestamp: new Date().toISOString() };
        this._saveCookieConsent(consent);
        this.showCookieConsent = false;
    }

    _handleCookieRejectAll() {
        const consent = { analytics: false, advertising: false, version: '1.0', timestamp: new Date().toISOString() };
        this._saveCookieConsent(consent);
        this.showCookieConsent = false;
    }

    _saveCookieConsent(consent) {
        try {
            localStorage.setItem('clarity_cookie_consent', JSON.stringify(consent));
            this._applyClarityConsent(consent);
        } catch (error) {
            logger.error('Failed to save cookie consent:', error.message);
        }
    }

    _applyClarityConsent(consent) {
        if (typeof window.clarity === 'function') {
            try {
                window.clarity('consentv2', {
                    ad_Storage: consent.advertising ? 'granted' : 'denied',
                    analytics_Storage: consent.analytics ? 'granted' : 'denied'
                });
            } catch {
                // Clarity not ready
            }
        }
    }

    firstUpdated() {
        // Scroll to bottom on initial load
        this._scrollToBottom();
    }

    updated(changedProperties) {
        // Scroll to bottom when messages change, loading state changes, or suggestions change
        if (
            changedProperties.has('messages') ||
            changedProperties.has('isLoading') ||
            changedProperties.has('suggestions')
        ) {
            this._scrollToBottom();
        }
    }

    /**
     * Scroll chat messages to bottom (newest messages)
     */
    _scrollToBottom() {
        requestAnimationFrame(() => {
            const messagesContainer = this.shadowRoot?.querySelector('.chatbox__messages');
            if (messagesContainer) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
        });
    }

    _restoreLocalMessageState() {
        const activeAddressCard = this.messages.find(
            (message) => message.type === MESSAGE_TYPES.ADDRESS_CARD && !message.disabled
        );
        if (activeAddressCard) {
            this._currentAddressCardId = activeAddressCard.id;
            this.isInputDisabled = true;
        } else {
            this._currentAddressCardId = null;
            this.isInputDisabled = false;
        }
    }

    _persistLastProjectContext(businessRequestId, ownerType = 'anonymous') {
        const tenantId = configService.get('TENANT_ID');
        if (!tenantId || !businessRequestId) {
            return;
        }

        setLastProjectContext({
            businessRequestId,
            tenantId,
            ownerType,
            source: 'widget',
            // Read the ids from the store (single source of truth) — the caller sets
            // them via setBusinessRequestContext / hydrateStructuredHistory first.
            // setLastProjectContext drops non-string values, so flags-off stays byte-parity.
            resourceApiPlanningApplicationId: chatStore.resourceApiPlanningApplicationId || undefined,
            resourceApiAssistConversationId: chatStore.resourceApiAssistConversationId || undefined
        });
    }

    _trackRequestController() {
        const controller = new AbortController();
        this._activeRequestControllers.add(controller);
        return {
            signal: controller.signal,
            release: () => {
                this._activeRequestControllers.delete(controller);
            }
        };
    }

    _abortActiveRequests() {
        for (const controller of this._activeRequestControllers) {
            controller.abort();
        }
        this._activeRequestControllers.clear();
    }

    async _restoreBusinessRequestHistory(businessRequestId) {
        if (!businessRequestId) {
            return false;
        }

        // WHY: capture the generation at entry — if a restart happens while
        // fetchChatHistory is in flight, the generation will diverge and we
        // must refuse to hydrate the cleared chat with stale backend data.
        const generation = this._resetGeneration;
        const request = this._trackRequestController();

        try {
            const history = await chatService.fetchChatHistory(businessRequestId, 50, { signal: request.signal });
            if (request.signal.aborted || generation !== this._resetGeneration) {
                return false;
            }
            if (!history?.success || history.source !== 'structured') {
                return false;
            }

            chatStore.hydrateStructuredHistory({
                businessRequestId,
                session: history.session,
                messages: history.messages || []
            });
            this._persistLastProjectContext(
                businessRequestId,
                history.session?.owner_type || chatStore.ownerType || 'anonymous'
            );
            chatStore.clearSuggestions();
            this.isInputDisabled = false;
            return true;
        } catch (error) {
            if (error?.name === 'AbortError') {
                return false;
            }
            logger.error('[DaisyChatbox] Failed to restore structured history:', error.message);
            return false;
        } finally {
            request.release();
        }
    }

    async _restoreContinuationState() {
        const continuation = getLastProjectContext();
        if (!continuation) {
            return false;
        }

        const tenantId = configService.get('TENANT_ID');
        if (
            continuation.source !== 'widget' ||
            !['anonymous', 'user'].includes(continuation.ownerType) ||
            !tenantId ||
            continuation.tenantId !== tenantId
        ) {
            clearLastProjectContext();
            return false;
        }

        // WHY: only require a live anon session when the continuation was
        // actually anon-owned. Auth continuations (ownerType === 'user') don't
        // use the anon session, so without this guard every signed-in user
        // hit _clearChatForNewSession here on every reload.
        if (continuation.ownerType !== 'user' && !anonBusinessRequestService.hasActiveSession()) {
            this._clearChatForNewSession();
            return false;
        }

        const restored = await this._restoreBusinessRequestHistory(continuation.businessRequestId);
        if (!restored) {
            this._clearChatForNewSession();
        }

        return restored;
    }
    _hasPropertyContext() {
        const state = userStore.getState();
        return !!(state.address && state.propId);
    }

    async _ensureBusinessRequest({ requirePropertyContext = false, forceCreate = false } = {}) {
        if (chatStore.businessRequestId) {
            return chatStore.businessRequestId;
        }

        if (this._businessRequestPromise) {
            return this._businessRequestPromise;
        }
        if (requirePropertyContext && !this._hasPropertyContext()) {
            return null;
        }

        if (!forceCreate && !this._hasPropertyContext()) {
            return null;
        }
        const tenantId = configService.get('TENANT_ID');
        const workflowId = configService.get('WORKFLOW_ID');

        if (!tenantId || !workflowId) {
            logger.error('[DaisyChatbox] Missing required configuration for BR creation:', { tenantId, workflowId });
            return null;
        }

        // WHY: capture generation before the long-lived creation promise.
        // If the user clicks restart during this await, the promise returns
        // null and we skip writing BR context — otherwise we'd silently hand
        // a freshly-created BR back into a cleared chat and keep stale state.
        const generation = this._resetGeneration;
        const request = this._trackRequestController();

        const creationPromise = (async () => {
            let creationResult = null;
            try {
                const userState = userStore.getState();
                const customData = anonBusinessRequestService.buildCustomData();
                creationResult = await anonBusinessRequestService.createAnonBusinessRequest(
                    {
                        tenantId,
                        workflowId,
                        email: userState.email || '',
                        name: userState.address || 'My Project',
                        customData
                    },
                    { signal: request.signal }
                );
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return null;
                }
                throw error;
            }

            if (request.signal.aborted || generation !== this._resetGeneration) {
                return null;
            }

            // The service returns the B1 capture wrapper
            // { businessRequest, resourceApi, storageTarget } (or null). Unwrap the
            // business request; tolerate a bare BR object so this stays resilient.
            const businessRequest = creationResult?.businessRequest ?? creationResult;
            const businessRequestId = businessRequest?.id || null;
            if (!businessRequestId) {
                return null;
            }

            const brContext = {
                businessRequestId,
                chatSessionId: chatStore.chatSessionId,
                conversationId: chatStore.conversationId,
                ownerType: chatStore.ownerType || 'anonymous'
            };
            // Push the captured ResourceAPI selectors into the store only when B0
            // returned them — flags-off responses add zero keys (byte-parity).
            const resourceApiPlanningApplicationId = creationResult?.resourceApi?.planningApplicationId || null;
            const resourceApiAssistConversationId = creationResult?.resourceApi?.assistConversationId || null;
            if (resourceApiPlanningApplicationId) {
                brContext.resourceApiPlanningApplicationId = resourceApiPlanningApplicationId;
            }
            if (resourceApiAssistConversationId) {
                brContext.resourceApiAssistConversationId = resourceApiAssistConversationId;
            }
            chatStore.setBusinessRequestContext(brContext);
            this._persistLastProjectContext(businessRequestId, chatStore.ownerType || 'anonymous');

            return businessRequestId;
        })().finally(() => {
            request.release();
            if (this._businessRequestPromise === creationPromise) {
                this._businessRequestPromise = null;
            }
        });

        this._businessRequestPromise = creationPromise;
        return creationPromise;
    }

    async _syncStructuredHistoryToBusinessRequest(businessRequestId, options = {}) {
        if (!businessRequestId) {
            return null;
        }

        // WHY: capture generation at scheduling time; the migration can chain
        // behind a previous one, so the effective delay before our state-writes
        // is unbounded. A mid-flight restart must not be re-hydrated.
        const generation = this._resetGeneration;
        const request = this._trackRequestController();

        const previousMigration = this._structuredMigrationPromises.get(businessRequestId) || Promise.resolve(null);
        const migrationPromise = previousMigration
            .catch(() => null)
            .then(async () => {
                let migrationResult = null;
                try {
                    migrationResult = await anonBusinessRequestService.migrateChatHistory(businessRequestId, {
                        ...options,
                        signal: request.signal
                    });
                } catch (error) {
                    if (error?.name === 'AbortError') {
                        return null;
                    }
                    throw error;
                }
                if (request.signal.aborted || generation !== this._resetGeneration) {
                    return migrationResult;
                }
                if (migrationResult?.session) {
                    chatStore.setBusinessRequestContext({
                        businessRequestId,
                        chatSessionId: migrationResult.session.chat_session_id || chatStore.chatSessionId,
                        conversationId: migrationResult.session.conversation_id || chatStore.conversationId,
                        ownerType: migrationResult.session.owner_type || chatStore.ownerType || 'anonymous'
                    });
                    this._persistLastProjectContext(
                        businessRequestId,
                        migrationResult.session.owner_type || chatStore.ownerType || 'anonymous'
                    );
                }

                return migrationResult;
            })
            .finally(() => {
                request.release();
                if (this._structuredMigrationPromises.get(businessRequestId) === migrationPromise) {
                    this._structuredMigrationPromises.delete(businessRequestId);
                }
            });

        this._structuredMigrationPromises.set(businessRequestId, migrationPromise);
        return migrationPromise;
    }

    _schedulePostAddressPersistence() {
        const generation = this._resetGeneration;

        const persistencePromise = (async () => {
            const businessRequestId =
                (await this._ensureBusinessRequest({ requirePropertyContext: true })) || chatStore.businessRequestId;
            if (generation !== this._resetGeneration || !businessRequestId) {
                return null;
            }

            return this._syncStructuredHistoryToBusinessRequest(businessRequestId);
        })()
            .catch((error) => {
                logger.warn('[DaisyChatbox] Failed to persist address chat context:', error?.message || error);
                return null;
            })
            .finally(() => {
                if (this._postAddressPersistencePromise === persistencePromise) {
                    this._postAddressPersistencePromise = null;
                }
            });

        this._postAddressPersistencePromise = persistencePromise;
        return persistencePromise;
    }

    render() {
        if (this.serviceUnavailable) {
            return html`
                <div class="chatbox" role="dialog" aria-label="DAISY Chatbot unavailable">
                    <chat-header
                        .showStartDA=${false}
                        .serviceUnavailable=${true}
                        @close=${this._handleClose}
                    ></chat-header>
                    <div class="chatbox__unavailable" role="status">
                        <h2 class="chatbox__unavailable-title">DAISY is temporarily unavailable</h2>
                        <p class="chatbox__unavailable-message">
                            This council's DAISY service is currently unavailable. Please contact the council directly
                            for planning and development assistance.
                        </p>
                    </div>
                </div>
            `;
        }

        return html`
            <div class="chatbox" role="dialog" aria-label="DAISY Chatbot">
                <chat-header
                    ?start-da-loading=${this.isCreatingBR}
                    @close=${this._handleClose}
                    @start-da-click=${this._handleStartDAClick}
                >
                </chat-header>

                ${this.systemAlertActive
                    ? html`
                          <div class="chatbox__alert-banner">
                              System downtime alert: Some features and services may be temporarily unavailable. We're
                              working to restore full functionality as soon as possible.
                          </div>
                      `
                    : ''}
                ${this.showCookieConsent
                    ? html`
                          <div class="chatbox__cookie-banner">
                              <div class="cookie-title">Cookie Preferences</div>
                              <div class="cookie-text">
                                  We use Microsoft Clarity to analyze user behavior and improve our service. You can
                                  manage your preferences or accept all cookies.
                              </div>
                              <div class="cookie-buttons">
                                  <button
                                      class="cookie-btn cookie-btn--secondary"
                                      @click=${this._handleCookieRejectAll}
                                  >
                                      Reject All
                                  </button>
                                  <button class="cookie-btn cookie-btn--primary" @click=${this._handleCookieAcceptAll}>
                                      Accept All
                                  </button>
                              </div>
                          </div>
                      `
                    : ''}

                <div class="chatbox__content">
                    <div class="chatbox__messages scrollbar-hide">
                        <div class="chatbox__disclaimer">
                            By messaging DAISY, you accept our
                            <a href="https://www.enterpriseaigroup.com/terms-of-use" target="_blank">Terms</a>
                            and
                            <a href="https://www.enterpriseaigroup.com/privacy-policy" target="_blank">Privacy Policy</a
                            >, and acknowledge DAISY may make mistakes. Check important information with council.
                        </div>
                        <chat-messages
                            .messages=${this.messages}
                            .suggestions=${this.suggestions}
                            .showFeedback=${true}
                            @message-feedback=${this._handleFeedback}
                            @address-selected=${this._handleAddressSelected}
                            @address-validating=${this._handleAddressValidating}
                            @address-validated=${this._handleAddressValidated}
                            @address-validation-error=${this._handleAddressValidationError}
                            @address-cancel=${this._handleAddressCancel}
                            @suggestion-click=${this._handleSuggestionClick}
                            @get-started-click=${this._handleGetStartedClick}
                            @click=${this._handleWelcomeStartClick}
                        >
                        </chat-messages>
                    </div>

                    <div class="chatbox__input-wrapper">
                        <button class="restart-btn" @click=${this._handleClear} aria-label="Restart chat">
                            ${this._renderRestartIcon()}
                        </button>
                        <div class="chatbox__input">
                            <chat-input
                                ?disabled=${this.isInputDisabled}
                                ?send-disabled=${this.isLoading || this.isCreatingBR || this.isStreaming}
                                placeholder=${this.isCreatingBR
                                    ? 'Please wait...'
                                    : this.isHandoverActive
                                      ? 'Message the live agent...'
                                      : 'Message...'}
                                @send-message=${this._handleSendMessage}
                            >
                            </chat-input>
                        </div>
                    </div>
                </div>

                ${this.showClearConfirm
                    ? html`
                          <div class="confirm-dialog" @click=${this._handleDialogBackdropClick}>
                              <div class="confirm-dialog__content" @click=${(e) => e.stopPropagation()}>
                                  <h3 class="confirm-dialog__title">Restart conversation?</h3>
                                  <p class="confirm-dialog__message">
                                      This will start a new conversation with DAISY. Your current chat history will be
                                      cleared.
                                  </p>
                                  <div class="confirm-dialog__actions">
                                      <button
                                          class="confirm-dialog__btn confirm-dialog__btn--cancel"
                                          @click=${this._cancelClear}
                                      >
                                          Cancel
                                      </button>
                                      <button
                                          class="confirm-dialog__btn confirm-dialog__btn--confirm"
                                          @click=${this._confirmClear}
                                      >
                                          Restart
                                      </button>
                                  </div>
                              </div>
                          </div>
                      `
                    : ''}
            </div>
        `;
    }

    /**
     * Render restart button SVG icon
     */
    _renderRestartIcon() {
        return html`
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
            </svg>
        `;
    }

    /**
     * Add welcome messages to chat (matching original app.js)
     */
    _addWelcomeMessages() {
        const portalUrl = this._generatePortalLaunchUrl().replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        const welcomeText1 = `Hi, I'm DAISY. Ask me anything about the development process or your property. If you click <a href="${portalUrl}" class="start-da-link" target="_blank" rel="noopener noreferrer"><i><b>Start now</b></i></a>, you can use my application identification tool, my document checklist tool and, receive your documents in a package that is ready for submission on the NSW Planning Portal.`;

        // Second welcome message
        const welcomeText2 = 'Please enter your address below to get started.';

        chatStore.addBotMessage(welcomeText1);
        chatStore.addBotMessage(welcomeText2);
    }

    /**
     * Handle close button click
     */
    _handleClose() {
        this.isOpen = false;
        this.dispatchEvent(
            new CustomEvent('chatbot-close', {
                bubbles: true,
                composed: true
            })
        );

        // Notify parent window (for iframe embedding)
        if (window.parent !== window) {
            const targetOrigin = this._getParentOrigin();
            window.parent.postMessage('closeIframe', targetOrigin);
        }
    }

    /**
     * Get the allowed parent origin for postMessage
     * Returns a specific origin instead of using '*' wildcard
     */
    _getParentOrigin() {
        // List of allowed parent origins for security
        const allowedOrigins = [
            'https://assist-chat.myenterprise.ai',
            'https://dev-assist-chat.myenterprise.ai',
            'https://test-assist-chat.myenterprise.ai',
            'https://www.enterpriseaigroup.com'
        ];

        // Try to get the parent origin from document.referrer
        try {
            if (document.referrer) {
                const referrerUrl = new URL(document.referrer);
                const referrerOrigin = referrerUrl.origin;

                // Check if referrer is in allowed list
                if (allowedOrigins.includes(referrerOrigin)) {
                    return referrerOrigin;
                }

                // Allow council websites (*.nsw.gov.au) and CMS preview (*.opencities.com)
                if (referrerUrl.hostname.endsWith('.nsw.gov.au') || referrerUrl.hostname.endsWith('.opencities.com')) {
                    return referrerOrigin;
                }

                // Allow same-origin parent
                if (referrerOrigin === window.location.origin) {
                    return referrerOrigin;
                }
            }
        } catch {
            // URL parsing failed, continue to fallback
        }

        // In development, allow localhost
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            return window.location.origin;
        }

        // Default to same origin if parent origin cannot be determined
        // This is safer than using '*'
        return window.location.origin;
    }

    /**
     * Handle clear chat - show confirmation dialog
     */
    _handleClear() {
        this.showClearConfirm = true;
    }

    /**
     * Confirm clear chat - actually perform the clear
     */
    _confirmClear() {
        // Hide dialog
        this.showClearConfirm = false;
        this._clearChatForNewSession();
    }

    /**
     * Cancel clear chat - hide dialog
     */
    _cancelClear() {
        this.showClearConfirm = false;
    }

    /**
     * Handle clicking on dialog backdrop - close dialog
     */
    _handleDialogBackdropClick(e) {
        if (e.target === e.currentTarget) {
            this.showClearConfirm = false;
        }
    }

    /**
     * Handle send message from input
     * @param {CustomEvent} e - Event with message detail
     */
    async _handleSendMessage(e) {
        const { message } = e.detail;

        if (!message?.trim()) return;

        if (this._historyRestorePromise) {
            await this._historyRestorePromise;
        }

        // Clear any existing suggestions
        chatStore.clearSuggestions();

        // If Genesys handover is active, route message to Genesys agent
        if (this.isHandoverActive) {
            genesysService.sendMessage(message);
            return;
        }

        // Add user message first so it's always visible
        const userMessageId = chatStore.addUserMessage(message);

        let businessRequestId = chatStore.businessRequestId;
        if (!businessRequestId && this._hasPropertyContext()) {
            businessRequestId = await this._ensureBusinessRequest({ requirePropertyContext: true });
            if (!businessRequestId) {
                chatStore.addBotMessage('I had trouble saving this project. Please try again.');
                return;
            }
        }

        if (businessRequestId) {
            void this._syncStructuredHistoryToBusinessRequest(businessRequestId, {
                excludeMessageIds: [userMessageId]
            }).catch((error) => {
                logger.warn('[DaisyChatbox] Failed to sync chat history before sending:', error?.message || error);
            });
        }

        // Send to chat service with message history for conversational context
        const { messages } = chatStore.getState();
        const generation = this._resetGeneration;
        const request = this._trackRequestController();
        try {
            await chatService.sendMessage(message, {
                messages,
                businessRequestId,
                userMessageId,
                signal: request.signal,
                shouldContinue: () => generation === this._resetGeneration
            });
        } finally {
            request.release();
        }
    }

    /**
     * Handle suggestions event from chat service
     * @param {CustomEvent} e - Event with suggestions
     */
    _handleSuggestionsEvent(e) {
        chatStore.setSuggestions(e.detail.suggestions || []);
    }

    /**
     * Handle next action event from chat service
     * @param {CustomEvent} e - Event with action details
     */
    _handleNextActionEvent(e) {
        const { action, result } = e.detail;

        switch (action) {
            case NEXT_ACTIONS.ENTER_ADDRESS:
                this._showAddressCard();
                break;
            case NEXT_ACTIONS.LOGIN:
                this._showLoginFlow();
                break;
            case NEXT_ACTIONS.GENESYS:
            case NEXT_ACTIONS.HANDOVER_GENESYS:
                this._initiateGenesysHandover();
                break;
            default:
                logger.debug('Unhandled next action:', action);
        }
    }

    /**
     * Handle suggestion click
     * @param {CustomEvent} e - Event with suggestion text
     */
    _handleSuggestionClick(e) {
        const { suggestion } = e.detail;
        chatStore.clearSuggestions(); // Clear suggestions

        // Handle Yes/No for "check another address" flow
        if (suggestion.toLowerCase() === 'yes') {
            // Add user message showing their selection
            chatStore.addUserMessage('Yes');
            // Show a new address card
            this._showAddressCard();
            return;
        } else if (suggestion.toLowerCase() === 'no') {
            // Add user message showing their selection
            chatStore.addUserMessage('No');
            // Unlock input and continue conversation (matching original app.js)
            this.isInputDisabled = false;
            chatStore.addBotMessageWithStreaming("Go ahead, I'm listening.");
            return;
        }

        // For other suggestions, send as regular message
        this._handleSendMessage({ detail: { message: suggestion } });
    }

    /**
     * Show address input card and disable input
     */
    _showAddressCard() {
        // Add address card as a message in the chat store
        this._currentAddressCardId = chatStore.addAddressCardMessage('', false);
        this.isInputDisabled = true;
    }

    /**
     * Handle address selected from autocomplete - sync text to store
     * @param {CustomEvent} e - Event with address detail
     */
    _handleAddressSelected(e) {
        const address = e.detail?.address;
        if (this._currentAddressCardId && address) {
            chatStore.updateAddressCardMessage(this._currentAddressCardId, {
                text: address
            });
        }
    }

    /**
     * Handle address validating event - show loading state with typing indicator
     */
    _handleAddressValidating() {
        chatStore.setLoading(true);
        // Create a streaming message to show typing dots while validating
        this._validatingMessageId = chatStore.createStreamingMessage();
    }

    /**
     * Handle address validation error - stop loading state
     */
    async _handleAddressValidationError(e) {
        const generation = this._resetGeneration;
        const shouldContinue = () => generation === this._resetGeneration;
        chatStore.setLoading(false);
        const reuseMessageId = this._validatingMessageId;
        this._validatingMessageId = null;

        if (e.detail?.restartRequired) {
            if (this._currentAddressCardId) {
                chatStore.updateAddressCardMessage(this._currentAddressCardId, {
                    text: e.detail.address,
                    disabled: true
                });
                this._currentAddressCardId = null;
            }
            this.isInputDisabled = true;
            chatStore.clearSuggestions();
            await chatStore.addBotMessageWithStreaming(
                e.detail.message || "I'm having trouble processing your address. Start a new chat to try again.",
                30,
                reuseMessageId,
                { shouldContinue }
            );
            return;
        }

        if (reuseMessageId) {
            chatStore.finalizeStreamingMessage(reuseMessageId);
        }
        // Track this failed validation attempt
        if (this._currentAddressCardId && e.detail?.address && !e.detail?.restartRequired) {
            chatStore.addInvalidAddressCard(this._currentAddressCardId, e.detail.address);
        }
    }

    /**
     * Handle address validated event
     * @param {CustomEvent} e - Event with property result
     */
    async _handleAddressValidated(e) {
        const generation = this._resetGeneration;
        const shouldContinue = () => generation === this._resetGeneration;
        chatStore.setLoading(false);
        // Keep the streaming message ID to reuse for the first response message
        const reuseMessageId = this._validatingMessageId;
        this._validatingMessageId = null;

        const { result } = e.detail;
        const address = sessionStorage.getItem('address') || result?.address || '';
        const currentCouncil = this._getOriginalCouncil()?.trim() || '';
        const returnedCouncilName = result?.council_name?.trim() || '';

        if (!shouldContinue()) return;

        // INVARIANT: unknown council data is not evidence of a council mismatch.
        if (!currentCouncil || !returnedCouncilName) {
            if (this._currentAddressCardId) {
                chatStore.updateAddressCardMessage(this._currentAddressCardId, {
                    text: address,
                    disabled: true
                });
            }
            this.isInputDisabled = true;
            chatStore.clearSuggestions();
            await chatStore.addBotMessageWithStreaming(
                "I couldn't confirm this property's council or retrieve its planning information. This may be temporary. Please restart the chat.",
                30,
                reuseMessageId,
                { shouldContinue }
            );
            return;
        }

        // Update the address card message to be disabled with the selected address
        if (this._currentAddressCardId) {
            chatStore.updateAddressCardMessage(this._currentAddressCardId, {
                text: address,
                disabled: true
            });
            this._currentAddressCardId = null;
        }

        if (!result || !shouldContinue()) return;

        // Get current council from URL/config (original source, not from userStore which may be overwritten)
        const returnedCouncilUrl = result.council_url || '';

        // Check if council matches
        const councilMatches =
            returnedCouncilName.toLowerCase().includes(currentCouncil.toLowerCase()) ||
            currentCouncil.toLowerCase().includes(returnedCouncilName.toLowerCase());

        if (councilMatches && returnedCouncilName) {
            // Council matches - store property data
            userStore.setProperty({
                address: result.address || address,
                propId: result.prop_id || result.propId,
                zone: result.zone,
                zoneDescription: result.zone_description,
                lotSize: result.lot_size,
                floorSpaceRatio: result.floor_space_ratio,
                mineSubsidenceAffects: result.mine_subsidence_affects,
                bushFireZone: result.bushfire_restrictions || false,
                floodZone: result.flood_restrictions || false,
                heritageZone: result.heritage_restrictions || false,
                activePrecinct: result.active_precinct || result.activePrecinct || '',
                propertyPrecinct: result.property_precinct || result.propertyPrecinct || null
            });

            // Store heritage item ID and property report URL if available
            if (result.heritage_item_id) {
                sessionStorage.setItem('Heritage_Item_ID', result.heritage_item_id);
                const heritageBaseUrl = configService.get('heritageMoreInfoBaseURL');
                const heritageMoreInfoUrl = `${heritageBaseUrl}${result.heritage_item_id}`;
                sessionStorage.setItem('Heritage_Property_More_Info_URL', heritageMoreInfoUrl);
            }
            if (result.property_report_url) {
                sessionStorage.setItem('propertyReport', result.property_report_url);
            }

            if (isOutsideResidentialScope(result.zone)) {
                await this._displayNonResidentialZoneMessage(reuseMessageId, { shouldContinue });
                return;
            }

            // Display property restrictions and report message (reuse streaming message)
            await this._displayPropertyValidationMessage(result, reuseMessageId, { shouldContinue });
            if (!shouldContinue()) return;

            this.isInputDisabled = false;
            void this._schedulePostAddressPersistence();
        } else {
            this.isInputDisabled = false;
            // Council does not match - show appropriate message
            // External link icon SVG (matching original app.js)
            const externalLinkSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" style="margin-left: 0.25em; height: 0.75em; color: black;"><path d="M10.5 2A.5.5 0 0 0 10 2.5v1a.5.5 0 0 0 .5.5h2.793L9.146 8.146a.5.5 0 0 0 .708.708L14 4.707V7.5a.5.5 0 0 0 1 0v-4A.5.5 0 0 0 14.5 3h-4z" fill="currentColor" stroke="currentColor" stroke-width="0.5"/><path d="M13.5 14a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11A.5.5 0 0 1 2 2h5a.5.5 0 0 0 0-1H2A1.5 1.5 0 0 0 .5 2.5v11A1.5 1.5 0 0 0 2 15h11a.5.5 0 0 1 .5.5z" fill="currentColor" stroke="currentColor" stroke-width="0.5"/></svg>`;

            // Check if council is in the supported "live" councils list from config
            const liveCouncils = window.mappingTable || [];
            const isLiveCouncil = liveCouncils.some(
                (council) => council.name && council.name.toLowerCase() === returnedCouncilName.toLowerCase()
            );

            // Generate email template URL for council notification
            const email_TemplateURL = configService.get('emailTemplateURL') || '';

            // Unique IDs for the notify council card
            const notifyCardId = `notifyCouncil-${Date.now()}`;
            const notifyBtnId = `notifyCouncilBtn-${notifyCardId}`;

            let botMessage = '';

            if (isLiveCouncil) {
                // Council is live - show website visit message
                botMessage = `<strong>${address}</strong> is not within this council. Upon checking, it is located in <strong>${returnedCouncilName}</strong>. The good news is, DAISY is already live with this council! Please visit their <a href="${returnedCouncilUrl}" target="_blank" style="color: #337ab7; text-decoration: none; font-weight: 500;">website${externalLinkSvg}</a> to access DAISY and continue your enquiry.`;
            } else if (!result.council_id || result.council_id.trim() === '') {
                // No council_id - council is not using this service, show notify option
                botMessage = `<strong>${address}</strong> is not within this council. It is located in <strong>${returnedCouncilName}</strong>.<br><br>It looks like <strong>${returnedCouncilName}</strong> isn't using this service yet. We can let them know (anonymously) that someone in their area searched for an address but wasn't able to use the service. This won't include your name or contact details — just that there's interest from residents.<br><br><a href="${email_TemplateURL}" target="_blank" rel="noopener noreferrer" style="color: #337ab7; text-decoration: none; font-weight: 500;">Preview message${externalLinkSvg}</a><br><div style="margin-top: 0.75rem;"><button id="${notifyBtnId}" class="notify-council-btn" data-id="${notifyCardId}">Send message to council</button></div>`;
            } else {
                // council_id exists - check if user is logged in
                const emailAddressFromURL = new URLSearchParams(window.location.search).get('Email') || '';
                const portalRedirectURL = configService.get('daisyPortalURL') || '';
                const switchCouncilRedirectURL = `${portalRedirectURL}/Council`;

                if (emailAddressFromURL?.trim() !== '') {
                    // Logged in – offer to switch
                    botMessage = `<strong>${address}</strong> is not within this council. Upon further checking, this is located in <strong>${returnedCouncilName}</strong>. Do you want to <a href="${switchCouncilRedirectURL}" target="_blank" style="color: #337ab7; text-decoration: none; font-weight: 500;">switch${externalLinkSvg}</a> to that council?`;
                } else {
                    // Not logged in – show notify option
                    botMessage = `<strong>${address}</strong> is not within this council. It is located in <strong>${returnedCouncilName}</strong>.<br><br>It looks like <strong>${returnedCouncilName}</strong> isn't using this service yet. We can let them know (anonymously) that someone in their area searched for an address but wasn't able to use the service. This won't include your name or contact details — just that there's interest from residents.<br><br><a href="${email_TemplateURL}" target="_blank" rel="noopener noreferrer" style="color: #337ab7; text-decoration: none; font-weight: 500;">Preview message${externalLinkSvg}</a><br><div style="margin-top: 0.75rem;"><button id="${notifyBtnId}" class="notify-council-btn" data-id="${notifyCardId}">Send message to council</button></div>`;
                }
            }

            // Display message with streaming animation (reuse streaming message)
            await chatStore.addBotMessageWithStreaming(botMessage, 30, reuseMessageId, { shouldContinue });
            if (!shouldContinue()) return;

            // Attach click handler for "Send message to council" button after streaming completes
            if (!isLiveCouncil) {
                requestAnimationFrame(() => {
                    const attemptToAttachHandler = (attempt = 1) => {
                        // Search through Shadow DOM to find the button
                        const button = this._findButtonInShadowDOM(notifyBtnId);
                        if (button) {
                            button.addEventListener('click', () =>
                                this._handleNotifyCouncil(notifyCardId, returnedCouncilName)
                            );
                        } else if (attempt < 5) {
                            setTimeout(() => attemptToAttachHandler(attempt + 1), 100);
                        }
                    };
                    attemptToAttachHandler();
                });
            }

            // Follow up message with Yes/No suggestions (with streaming)
            await new Promise((resolve) => setTimeout(resolve, 500));
            if (!shouldContinue()) return;
            await chatStore.addBotMessageWithStreaming('Do you want to check on another address?', 30, null, {
                shouldContinue
            });
            if (!shouldContinue()) return;
            // Show Yes/No suggestions after the message
            await new Promise((resolve) => setTimeout(resolve, 300));
            if (!shouldContinue()) return;
            this.isInputDisabled = true;
            chatStore.setSuggestions(['Yes', 'No']);
        }
    }

    async _displayNonResidentialZoneMessage(reuseMessageId = null, options = {}) {
        const shouldContinue = () => options.shouldContinue?.() ?? true;

        await chatStore.addBotMessageWithStreaming(
            "I've checked your address, but this property is in a zone that does not support residential development. I am only able to assist with residential development.",
            30,
            reuseMessageId,
            { shouldContinue }
        );
        if (!shouldContinue()) return;

        await new Promise((resolve) => setTimeout(resolve, 500));
        if (!shouldContinue()) return;

        await chatStore.addBotMessageWithStreaming('Do you want to check on another address?', 30, null, {
            shouldContinue
        });
        if (!shouldContinue()) return;

        await new Promise((resolve) => setTimeout(resolve, 300));
        if (!shouldContinue()) return;

        this.isInputDisabled = true;
        chatStore.setSuggestions(['Yes', 'No']);
    }

    /**
     * Display property validation message based on restrictions
     * @param {Object} propertyData - Property validation result
     * @param {string} [reuseMessageId=null] - Optional streaming message ID to reuse
     */
    async _displayPropertyValidationMessage(propertyData, reuseMessageId = null, options = {}) {
        const shouldContinue = () => options.shouldContinue?.() ?? true;
        // External link SVG icon
        const externalLinkSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" style="margin-left: 0.25em; height: 0.75em; color: black;"><path d="M10.5 2A.5.5 0 0 0 10 2.5v1a.5.5 0 0 0 .5.5h2.793L9.146 8.146a.5.5 0 0 0 .708.708L14 4.707V7.5a.5.5 0 0 0 1 0v-4A.5.5 0 0 0 14.5 3h-4z" fill="currentColor" stroke="currentColor" stroke-width="0.5"/><path d="M13.5 14a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11A.5.5 0 0 1 2 2h5a.5.5 0 0 0 0-1H2A1.5 1.5 0 0 0 .5 2.5v11A1.5 1.5 0 0 0 2 15h11a.5.5 0 0 1 .5.5z" fill="currentColor" stroke="currentColor" stroke-width="0.5"/></svg>`;

        // Build every link through one empty-safe helper: a missing URL renders
        // as '' (never a dead href="undefined" or href=""), and the sentence
        // guards below drop the clause when the link is ''. Config links
        // (practitioners/consultants) have hard defaults in environment.js; the
        // data links (property report, heritage more-info) are the ones that
        // legitimately go empty.
        const externalLink = (url, text) =>
            url ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}${externalLinkSvg}</a>` : '';

        const bushfirePractitionerURL = externalLink(
            configService.get('bushfirePractitionersURL'),
            'accredited practitioners'
        );
        const heritageConsultantURL = externalLink(
            configService.get('heritageConsultantsURL'),
            'accredited practitioners'
        );
        const propertyReportURL = externalLink(propertyData?.property_report_url, 'property report');
        const heritageMoreInfoURL = externalLink(
            sessionStorage.getItem('Heritage_Property_More_Info_URL'),
            'more information'
        );

        // Per-council frontend toggle: a hazard disabled for this council is
        // treated as not part of the data (not-present + not-assessed), so it is
        // dropped from the message regardless of the property report. Mirrors the
        // DAISY app's features.riskIndicators tenant config.
        const enabled = getEnabledRiskIndicators(this._getOriginalCouncil());

        // Check for restrictions (present = the hazard applies to the property)
        const hasBushfire = enabled.bushfire && propertyData?.bushfire_restrictions === true;
        const hasFlood = enabled.flood && propertyData?.flood_restrictions === true;
        const hasHeritage = enabled.heritage && propertyData?.heritage_restrictions === true;

        // Was each hazard layer actually assessed for this property? Driven by the
        // property report data (not the council name): flood via its lookup flag,
        // bushfire/heritage via tri-state (null = not assessed). A not-assessed
        // hazard must never be reported as "no <hazard> restrictions".
        const bushfireAssessed = enabled.bushfire && propertyData?.bushfire_restrictions != null;
        const floodAssessed = enabled.flood && propertyData?.flood_lookup_successful === true;
        const heritageAssessed = enabled.heritage && propertyData?.heritage_restrictions != null;

        let message = '';

        // Determine the appropriate response based on restrictions
        if (hasBushfire && !hasFlood && !hasHeritage) {
            // 1️⃣ BUSHFIRE ONLY
            message = 'This property looks to be in bushfire-prone land. ';
            message += `You may need to get a <strong>Bushfire Assessment Report</strong>. While I can't advise you on who to use for the report, you can look up ${bushfirePractitionerURL}.`;
        } else if (hasBushfire && hasFlood && hasHeritage) {
            // 2️⃣ BUSHFIRE + FLOOD + HERITAGE
            message = 'I can see that you have bushfire, flood, and heritage restrictions. ';
            message += `You may need a <strong>Bushfire Assessment Report</strong> from ${bushfirePractitionerURL}, a <strong>Flood Certificate</strong>, and a <strong>Heritage Report</strong> from ${heritageConsultantURL}. `;
            message += 'I recommend speaking to your local council to understand if you need any other documentation.';
        } else if (hasBushfire && hasHeritage && !hasFlood) {
            // 3️⃣ HERITAGE + BUSHFIRE
            message = 'This property looks to have some heritage and bushfire restrictions. ';
            message += `You may need a <strong>Heritage Report</strong> from an ${heritageConsultantURL} and you may need to get a <strong>Bushfire Assessment Report</strong>. `;
            message += `While I can't advise you on who to use for the report, you can look up ${bushfirePractitionerURL}.`;
        } else if (hasBushfire && hasFlood && !hasHeritage) {
            // 4️⃣ BUSHFIRE + FLOOD
            message = 'This property looks to have some flood and bushfire restrictions. ';
            message +=
                'You may need a <strong>Flood Certificate</strong> and a <strong>Bushfire Assessment Report</strong>, among other required documents. ';
            message += `For accredited practitioners, visit ${bushfirePractitionerURL}.`;
        } else if (!hasBushfire && hasFlood && hasHeritage) {
            // 5️⃣ FLOOD + HERITAGE
            message = 'This property looks to have some flood and heritage restrictions. ';
            message += 'You may need a <strong>Flood Certificate</strong>, among other required documents. ';
            message += 'I recommend speaking to your local council to understand what other documents you may need. ';
            message += `You may also require a <strong>Heritage Report</strong> from ${heritageConsultantURL}.`;
        } else if (!hasBushfire && !hasFlood && hasHeritage) {
            // 6️⃣ HERITAGE ONLY
            message = 'This property looks to have some heritage restrictions. ';
            message += `You may need a <strong>Heritage Report</strong> from ${heritageConsultantURL}.`;
            // Only invite "find out more" when the property report gave a heritage
            // item ID; without it heritageMoreInfoURL is '' and the sentence dangles.
            if (heritageMoreInfoURL) {
                message += ` You can find out ${heritageMoreInfoURL}.`;
            }
        } else if (!hasBushfire && hasFlood && !hasHeritage) {
            // 7️⃣ FLOOD ONLY
            message = 'This property looks to be in a flood-prone area. ';
            message += 'You may need a <strong>Flood Certificate</strong> among other required documents. ';
            message += 'I recommend speaking to your local council to check if additional documents are needed.';
        } else {
            const assessedList = [
                bushfireAssessed ? 'bushfire' : null,
                floodAssessed ? 'flood' : null,
                heritageAssessed ? 'heritage' : null
            ].filter(Boolean);
            // 2 items use "a or b" (no comma, per standard English); 3+ items
            // use an Oxford-style ", or" before the last.
            let joined;
            if (assessedList.length <= 1) {
                joined = assessedList[0] || '';
            } else if (assessedList.length === 2) {
                joined = `${assessedList[0]} or ${assessedList[1]}`;
            } else {
                joined = `${assessedList.slice(0, -1).join(', ')}, or ${assessedList[assessedList.length - 1]}`;
            }
            message = joined
                ? `I've checked your property and based on available data, found no ${joined} restrictions.`
                : '';
        }

        // Add property report if available
        if (propertyReportURL) {
            if (hasBushfire || hasFlood || hasHeritage) {
                message += `<br><br>I've also found a ${propertyReportURL} that you may find useful!`;
            } else {
                message += `${message ? ' ' : ''}I've also found a ${propertyReportURL} that you may find useful!`;
            }
        }

        // INVARIANT: disabled hazards and unrelated enrichment failures must not trigger this notice.
        const unavailableHazards = [
            enabled.bushfire && !bushfireAssessed ? 'bushfire' : null,
            enabled.flood && !floodAssessed ? 'flood' : null,
            enabled.heritage && !heritageAssessed ? 'heritage' : null
        ].filter(Boolean);
        const hasUnavailableHazards = unavailableHazards.length > 0;
        const reportLinkUnavailable = propertyData.report_link_unavailable && !propertyReportURL;
        let notice = '';
        if (hasUnavailableHazards && reportLinkUnavailable) {
            notice = `Some NSW planning information (${unavailableHazards.join(', ')}) including the property link is temporarily unavailable.`;
        } else if (reportLinkUnavailable) {
            notice = 'Some NSW planning information like the property link is temporarily unavailable.';
        } else if (hasUnavailableHazards) {
            notice = `Some NSW planning information (${unavailableHazards.join(', ')}) is temporarily unavailable.`;
        }
        if (notice) {
            const warning = `<strong>${notice}</strong>`;
            message = message ? `${message}<br><br>${warning}` : warning;
        }

        // Display the message with streaming animation (reuse streaming message if provided)
        if (message) {
            await chatStore.addBotMessageWithStreaming(message, 30, reuseMessageId, { shouldContinue });
        }
        if (!shouldContinue()) return;

        // Wait a moment before follow-up message
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (!shouldContinue()) return;

        const followUpMessage =
            'Now that I have your address, I can provide more accurate property details. What would you like to know about your property?';
        await chatStore.addBotMessageWithStreaming(followUpMessage, 30, null, { shouldContinue });
    }

    /**
     * Handle address cancel
     */
    _handleAddressCancel() {
        // Stop loading state if it was active
        chatStore.setLoading(false);

        // Remove the address card message if cancelled
        if (this._currentAddressCardId) {
            // Could remove the message here if needed
            this._currentAddressCardId = null;
        }
        this.isInputDisabled = false; // Re-enable input
    }

    /**
     * Get original council from URL/config (not from userStore which may be overwritten by API)
     * @returns {string} - Original council name
     */
    _getOriginalCouncil() {
        // Check DAISY_CONFIG first (set by eaidev.html)
        if (window.DAISY_CONFIG?.council) {
            return window.DAISY_CONFIG.council;
        }

        // Check URL parameters directly
        const urlParams = new URLSearchParams(window.location.search);
        const urlCouncil = urlParams.get('Council') || urlParams.get('council');
        if (urlCouncil) return urlCouncil;

        // Check session storage only after current embed context.
        const daisyCouncil = sessionStorage.getItem('daisy_council');
        if (daisyCouncil) return daisyCouncil;

        return '';
    }

    /**
     * Find an element by ID within Shadow DOM hierarchy
     * Traverses: daisy-chatbox -> chat-messages -> message-bubble -> button
     * @param {string} id - Element ID to find
     * @returns {HTMLElement|null} - Found element or null
     */
    _findButtonInShadowDOM(id) {
        const selector = `#${CSS.escape(id)}`;

        // Search function that goes through all shadow roots
        const deepSearch = (root) => {
            if (!root) return null;

            // Try direct querySelector first
            let found = root.querySelector?.(selector);
            if (found) return found;

            // Get all elements and check their shadow roots
            const elements = root.querySelectorAll?.('*') || [];
            for (const el of elements) {
                if (el.shadowRoot) {
                    found = deepSearch(el.shadowRoot);
                    if (found) return found;
                }
            }

            return null;
        };

        // Start from this component's shadow root
        return deepSearch(this.shadowRoot);
    }

    /**
     * Handle "Send message to council" button click
     * @param {string} cardId - ID of the notify card
     * @param {string} councilName - Name of the council to notify
     */
    async _handleNotifyCouncil(cardId, councilName) {
        const btnId = `notifyCouncilBtn-${cardId}`;
        const btn = this._findButtonInShadowDOM(btnId);

        if (!btn) {
            logger.warn(`Council message button not found: ${btnId}`);
            return;
        }

        try {
            // Disable button and show sending state. The disabled attribute
            // governs interaction; styling is token-class driven, not inline.
            btn.disabled = true;
            btn.textContent = 'Sending...';

            // Build payload — searchedAddress may be '' when no address searched.
            const searchedAddress = sessionStorage.getItem('address') || '';
            const payload = {
                councilName,
                searchedAddress
            };

            // Route through the BFF proxy. apiService.request returns the raw
            // Response so the LIVE_WITH_COUNCIL 500-body sniff below still works;
            // it also injects the server-authoritative tenant + anon-session +
            // session headers and owns the single 401 reinit-retry (pinned by
            // api.service.test UT-API-004/-005/-007).
            const response = await apiService.request(getEndpoint('sendEmail'), {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                // A 500 whose body names the "live with the council" case is a
                // distinct outcome, not a generic failure.
                if (response.status === 500) {
                    const errorText = await response.text();
                    if (errorText.toLowerCase().includes('live with the council')) {
                        throw new Error('LIVE_WITH_COUNCIL');
                    }
                }
                throw new Error(`HTTP ${response.status}`);
            }

            // Success: replace button with success label
            if (btn.parentElement) {
                const successEl = document.createElement('p');
                successEl.className = 'notify-council-status';
                successEl.textContent = 'Email sent successfully!';
                btn.parentElement.replaceChild(successEl, btn);
            }
        } catch (error) {
            logger.error('Failed to send message to council:', error.message);

            // Check if this is the specific "live with council" error
            if (error.message === 'LIVE_WITH_COUNCIL') {
                if (btn.parentElement) {
                    const liveEl = document.createElement('p');
                    liveEl.className = 'notify-council-status';
                    liveEl.textContent =
                        'We are already live with this council, please visit their website to access DAISY!';
                    btn.parentElement.replaceChild(liveEl, btn);
                }
            } else {
                // Re-enable button for other errors
                btn.disabled = false;
                btn.textContent = 'Try again';
            }
        }
    }

    /**
     * Show login flow - displays Get Started card with Portal URL
     */
    async _showLoginFlow() {
        // Add Get Started card as a message in the chat (like address card)
        chatStore.addGetStartedCardMessage();
    }

    /**
     * Initiate Genesys handover
     */
    async _initiateGenesysHandover() {
        const success = await genesysService.initiateHandover({
            name: userStore.getState().firstName || 'DAISY User',
            email: userStore.getState().email || ''
        });

        if (!success) {
            chatStore.addBotMessage(
                "I'm sorry, I couldn't connect you to a live agent right now. " +
                    'Please try again later or continue chatting with me.'
            );
        }
    }

    /**
     * Handle get started card click - create BR and redirect
     * @param {CustomEvent} e - Event with messageId
     */
    _handleGetStartedClick(e) {
        const { messageId } = e.detail;
        chatStore.updateGetStartedCardLoading(messageId, true);
        this._createBRAndRedirect({ messageId });
    }

    _handleWelcomeStartClick(event) {
        const link = event
            .composedPath()
            .find((node) => node instanceof HTMLAnchorElement && node.classList.contains('start-da-link'));
        if (!link) return;

        // INVARIANT: use the header handoff so the current property and chat reach the portal.
        event.preventDefault();
        this._handleStartDAClick();
    }

    _handleStartDAClick() {
        this._createBRAndRedirect();
    }

    /**
     * Create anonymous business request and redirect to loading page
     * @param {Object} options - Options
     * @param {string} options.messageId - Get started card message ID (optional)
     */
    async _createBRAndRedirect(options = {}) {
        const { messageId } = options;
        const errorMsg = 'Something went wrong. Please try again.';

        if (this.isCreatingBR) return;

        // Open a blank popup immediately to avoid popup blockers. The signed
        // handoff POST should own the first real navigation so we do not race
        // a GET loading page against the handoff adoption request.
        const popupTarget = `daisy-widget-${Date.now()}`;
        const newWindow = window.open('about:blank', popupTarget);

        // Show a loading state while BR creation + handoff token are in flight
        if (newWindow && newWindow.document) {
            newWindow.document.write(
                '<!doctype html><html><head><title>Loading...</title></head><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui,sans-serif;color:#555"><p>Loading your project\u2026</p></body></html>'
            );
            newWindow.document.close();
        }

        this.isCreatingBR = true;

        try {
            const businessRequestId =
                (await this._ensureBusinessRequest({ forceCreate: true })) || chatStore.businessRequestId;
            if (!businessRequestId) {
                this._navigateToPortal({
                    target: newWindow ? popupTarget : '_self',
                    popupWindow: newWindow || null
                });

                if (messageId) {
                    chatStore.removeGetStartedCardMessage(messageId);
                }

                this._clearChatForNewSession();
                this._handleClose();

                logger.debug('[DaisyChatbox] Redirected to DAISY without BR context', {
                    popupTarget
                });
                return;
            }

            const migrationResult = await this._syncStructuredHistoryToBusinessRequest(businessRequestId);
            if (migrationResult === null) {
                throw new Error('Business request chat history migration failed');
            }

            const handoffRequest = this._trackRequestController();
            let handoffToken = null;
            try {
                handoffToken = await anonBusinessRequestService.createHandoffToken(businessRequestId, {
                    signal: handoffRequest.signal
                });
            } finally {
                handoffRequest.release();
            }
            if (!handoffToken) {
                throw new Error('Business request handoff token was not created');
            }

            this._submitHandoffForm({
                handoffToken,
                target: newWindow ? popupTarget : '_self',
                popupWindow: newWindow || null
            });

            // Remove the get started card if applicable
            if (messageId) {
                chatStore.removeGetStartedCardMessage(messageId);
            }

            // Clear chat history to start fresh
            this._clearChatForNewSession();

            // Close the chatbot after successful redirect
            this._handleClose();

            logger.debug('[DaisyChatbox] BR created and redirected:', {
                businessRequestId,
                popupTarget
            });
        } catch (error) {
            logger.error('[DaisyChatbox] Failed to create business request:', error.message);
            if (newWindow) newWindow.close();
            if (messageId) {
                chatStore.updateGetStartedCardError(messageId, errorMsg);
            } else {
                chatStore.addBotMessage(errorMsg);
            }
        } finally {
            this.isCreatingBR = false;
        }
    }

    /**
     * Generate redirect URL for the loading page with business request ID
     * @param {string|null} businessRequestId - Business request ID
     * @returns {string} - Redirect URL
     */
    _generateRedirectUrl(businessRequestId) {
        const baseUrl = configService.get('portalUrl', 'http://localhost:3000');
        const url = new URL(baseUrl);

        // add loading parameter while BR is empty
        url.searchParams.append('loading', 'BR');

        if (businessRequestId) {
            url.searchParams.append('brid', businessRequestId);
        }

        // Add council info
        const state = userStore.getState();
        const councilName = state.councilName || configService.getCouncilFromUrl() || '';
        if (councilName) {
            url.searchParams.append('council', councilName);
        }

        return url.toString();
    }

    /**
     * Build the DAISY handoff endpoint URL.
     * @returns {string} - Absolute handoff URL
     */
    _generateHandoffActionUrl() {
        const baseUrl = configService.get('portalUrl', 'http://localhost:3000');
        return new URL('/api/eai/widget-handoff/adopt', baseUrl).toString();
    }

    /**
     * Build the base DAISY portal URL without widget-loading params.
     * Used when the user clicks Start now before any BR/property context exists.
     * @returns {string} - Absolute portal URL
     */
    _generatePortalLaunchUrl() {
        const baseUrl = configService.get('portalUrl', 'http://localhost:3000');
        const url = new URL(baseUrl);

        const state = userStore.getState();
        const councilName = state.councilName || configService.getCouncilFromUrl() || '';
        if (councilName) {
            url.searchParams.append('council', councilName);
        }

        return url.toString();
    }

    /**
     * Navigate to the DAISY portal directly.
     * @param {Object} options - Navigation options
     * @param {string} options.target - Window target name
     * @param {Window|null} [options.popupWindow] - Pre-opened popup window
     */
    _navigateToPortal(options) {
        const { target, popupWindow = null } = options;
        const url = this._generatePortalLaunchUrl();

        if (popupWindow && !popupWindow.closed) {
            popupWindow.location.href = url;
            return;
        }

        if (target === '_self') {
            window.location.href = url;
            return;
        }

        window.open(url, target);
    }

    /**
     * POST the signed handoff token to DAISY without exposing it in the URL.
     * @param {Object} options - Submit options
     * @param {string} options.handoffToken - Signed handoff token
     * @param {string} options.target - Window target name
     * @param {Window|null} [options.popupWindow] - Pre-opened popup window
     */
    _submitHandoffForm(options) {
        const { handoffToken, target, popupWindow = null } = options;
        const actionUrl = this._generateHandoffActionUrl();

        if (popupWindow && !popupWindow.closed && popupWindow.document) {
            const escapedToken = handoffToken.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
            const popupHtml = `<!doctype html><html><body><form id="handoff-form" method="POST" action="${actionUrl}"><input type="hidden" name="handoff_token" value="${escapedToken}" /></form><script>document.getElementById('handoff-form').submit();</script></body></html>`;

            popupWindow.document.open();
            popupWindow.document.write(popupHtml);
            popupWindow.document.close();
            return;
        }

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = actionUrl;
        form.target = target;
        form.style.display = 'none';

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'handoff_token';
        input.value = handoffToken;
        form.appendChild(input);

        document.body.appendChild(form);
        form.submit();
        form.remove();
    }

    /**
     * Clear chat for a new session (after redirect to portal)
     */
    _clearChatForNewSession() {
        // WHY: bump BEFORE clearing so any in-flight async work that resolves
        // after this point sees a generation mismatch and refuses to write.
        // Without this, _restoreBusinessRequestHistory / _ensureBusinessRequest /
        // _syncStructuredHistoryToBusinessRequest can silently re-populate the
        // just-cleared state via hydrateStructuredHistory / setBusinessRequestContext.
        this._resetGeneration++;
        this._abortActiveRequests();
        clearLastProjectContext();
        anonBusinessRequestService.clearSession();
        chatStore.clearMessages();
        chatStore.setLoading(false);
        chatStore.resetConversationContext();
        userStore.clearProperty();
        userStore.setCurrentProject(null);
        sessionStore.remove('projectDataObject');
        sessionStore.remove('policiesDataObject');
        chatStore.clearSuggestions();
        this._historyRestorePromise = null;
        this._businessRequestPromise = null;
        this._postAddressPersistencePromise = null;
        this._structuredMigrationPromises.clear();
        this._currentAddressCardId = null;
        this.isInputDisabled = false;

        // Clear the input field
        this.shadowRoot?.querySelector('chat-input')?.clear();

        // Reset to initial state with welcome messages
        this._addWelcomeMessages();
        this._showAddressCard();

        logger.debug('[DaisyChatbox] Chat cleared for new session');
    }

    /**
     * Handle message feedback (thumbs up/down)
     * @param {CustomEvent} e - Event with feedback detail
     */
    async _handleFeedback(e) {
        const { messageId, persistedMessageId, feedback } = e.detail;
        const businessRequestId = chatStore.businessRequestId;
        if (!businessRequestId || !persistedMessageId) {
            return;
        }

        const ok = await chatService.sendFeedback({
            businessRequestId,
            messageId: persistedMessageId,
            feedback
        });

        if (ok) {
            chatStore.updateMessageMetadata(messageId, {
                feedback: feedback === 'up' ? 'good' : 'bad'
            });
        }
    }

    /**
     * Open the chatbot
     */
    open() {
        this.isOpen = true;
        this._scrollToBottom();
    }

    /**
     * Close the chatbot
     */
    close() {
        this.isOpen = false;
    }

    /**
     * Toggle chatbot open/closed
     */
    toggle() {
        this.isOpen = !this.isOpen;
    }
}

customElements.define('daisy-chatbox', DaisyChatbox);
